import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowModel,
  Row,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { DataTablePagination } from './data-table-pagination';
import { DataTableViewOptions } from './data-table-view-options';
import { Input } from '../input';
import { ExportExcel } from './export-excel';
import { Skeleton } from './data-table-skeleton';
import { Button } from '../ButtonGs';
import { Pagination } from 'interfaces/pagination';

export type RenderDataTableTopBarProps<T> = {
  selectedRows: RowModel<T>;
  totalRows: number;
};

interface DataTableProps<TData, TValue = unknown> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterBy?: string;
  exportTable?: boolean;
  exportTableData?: any[];
  exportFileName?: string;
  isLoading?: boolean;
  showControlColumns?: boolean;
  pagination?: Partial<Pagination>;
  selectable?: boolean;
  selectionAction?: (data: RowModel<TData>, selectedIds: number[]) => void;
  selectionActionLabel?: string;
  onQueryExport?: () => Promise<any>;
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  manualPagination?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (columnVisibility: VisibilityState) => void;
  onRowClick?: (row: Row<TData>) => void;
  renderTopBar?: (props: RenderDataTableTopBarProps<TData>) => React.ReactNode;
}

export type DataTableRef = {
  clearSelection: () => void;
};

export const DataTable2 = React.forwardRef<DataTableRef, DataTableProps<any>>(
  <TData, TValue>(
    {
      columns,
      data,
      filterBy,
      exportTable = false,
      exportTableData = [],
      isLoading = false,
      showControlColumns = true,
      selectable = false,
      exportFileName,
      manualPagination = false,
      pagination = {
        page: 1,
        perPage: 10,
      },
      onQueryExport,
      onPaginationChange,
      selectionAction,
      selectionActionLabel,
      columnVisibility: initialColumnVisibility = {},
      onColumnVisibilityChange,
      onRowClick,
      renderTopBar,
    }: DataTableProps<TData, TValue>,
    ref: React.Ref<DataTableRef>
  ) => {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] =
      React.useState<ColumnFiltersState>([]);
    const [paginationFilter, setPaginationFilter] = React.useState({
      pageIndex: pagination?.page ? pagination.page - 1 : 0,
      pageSize: pagination.perPage || 10,
    });
    const [columnVisibility, setColumnVisibility] =
      React.useState<VisibilityState>(initialColumnVisibility);
    const [rowSelection, setRowSelection] = React.useState<
      Record<string, boolean>
    >({});

    const exportData = exportTableData.length ? exportTableData : data;

    const updateColumnVisibility = React.useCallback(
      (state: VisibilityState) => {
        onColumnVisibilityChange?.(state);
      },
      [onColumnVisibilityChange]
    );

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      //@ts-ignore
      getRowId: (row) => row.id,
      onSortingChange: setSorting,
      onPaginationChange: setPaginationFilter,
      onRowSelectionChange: setRowSelection,
      manualPagination: manualPagination,
      pageCount: pagination?.lastPage,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
        pagination: paginationFilter,
      },
      initialState: {
        pagination: paginationFilter,
      },
    });

    const TopBarComponent = React.useMemo(() => {
      if (!renderTopBar) return null;
      const selectedRows = table.getFilteredSelectedRowModel();
      const totalRows = table.getFilteredRowModel().rows.length;
      return renderTopBar({
        selectedRows,
        totalRows,
      });
    }, [renderTopBar, rowSelection]);

    const selectionCounterText = React.useMemo(() => {
      const selectable = manualPagination
        ? pagination?.total || table.getFilteredRowModel().rows.length
        : table.getFilteredRowModel().rows.length;

      const selected = manualPagination
        ? Object.keys(rowSelection).length
        : table.getFilteredSelectedRowModel().rows.length;

      return `(${selected}/${selectable})`;
    }, [table, pagination, rowSelection, manualPagination]);

    React.useEffect(() => {
      updateColumnVisibility(columnVisibility);
    }, [columnVisibility, updateColumnVisibility]);

    React.useEffect(() => {
      onPaginationChange &&
      data.length &&
      onPaginationChange({
        page: paginationFilter.pageIndex + 1,
        limit: paginationFilter.pageSize,
      });
    }, [paginationFilter]);

    const handleSelectionAction = React.useCallback(() => {
      const tableSelectedModel = table.getFilteredSelectedRowModel();
      const selectedIds = Object.keys(rowSelection).map(Number);
      selectionAction?.(tableSelectedModel, selectedIds);
    }, [rowSelection, selectionAction, table]);

    React.useImperativeHandle(ref, () => ({
      clearSelection: () => {
        setRowSelection({});
      },
    }), []);

    if (isLoading) {
      return <Skeleton />;
    }

    if (!data.length) {
      return (
        <div className="flex items-center justify-center h-24 text-center font-sans">
          Sem resultados.
        </div>
      );
    }

    return (
      <div>
        <div className="w-full mb-4 flex flex-col md:flex-row gap-3 items-center justify-between">
          {filterBy && (
            <Input
              placeholder={`Filtrar ${filterBy.toLowerCase()} ...`}
              value={
                (table.getColumn(filterBy)?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table.getColumn(filterBy)?.setFilterValue(event.target.value)
              }
              className="max-w-sm focus-visible:ring-primary-500"
            />
          )}
          {selectionAction && (
            <Button.Root
              size="sm"
              onClick={handleSelectionAction}
              disabled={!Object.keys(rowSelection).length}
            >
              {selectionActionLabel ?? 'Action'} {selectionCounterText}
            </Button.Root>
          )}
          {TopBarComponent}
          <div className="flex flex-1 gap-3 w-full justify-end">
            {exportTable && (
              <ExportExcel
                data={exportData}
                exportQuery={onQueryExport}
                fileName={exportFileName}
              />
            )}
            {showControlColumns && <DataTableViewOptions table={table} />}
          </div>
        </div>
        <div className="rounded-md border bg-white">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="text-xs font-sans">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={() => onRowClick?.(row)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="p-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center font-sans"
                  >
                    Sem resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-start space-x-2 py-4">
          {selectable && (
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{' '}
              {table.getFilteredRowModel().rows.length} linha(s) selecionada(s).
            </div>
          )}
          <DataTablePagination table={table} />
        </div>
      </div>
    );
  }
);
DataTable2.displayName = 'DataTable';
